import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import { cacher } from 'app/cacheUtils/cacheIndex';
import { RootState } from 'app/store';
import { queryApi } from './queryApi';
import { Webhook, WebhookDataField } from 'features/webhooks/webhooks.models';
import { DataScienceTaskStatus } from 'features/data-science/tasks-status';
import {
  DataScienceTask,
  DataScienceTaskParametersDto
} from 'features/data-science/tasks.models';
import {
  ExclusionTypeLabel,
  exclusionTypeLabelToTypeMap
} from 'features/exclusions/exclusions.enums';
import { selectIsSandboxUser } from 'features/auth/authenticationSlice';
import {
  CreateSegmentationConfigRequestDto,
  UpdateSegmentationConfigRequestDto
} from 'features/segmentation-config/segmentation-config.types';
import { TabKey } from 'features/segmentation-config/segmentation-config-tab-keys';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

export interface DeleteOpportunityRequest {
  opportunityId: string;
  opportunityDateTime: string;
}

export interface DeleteRevenueRequest {
  revenueId: string;
  revenueClosedDateTime: string;
}

const defaultSuccessResult = { data: 'success' };

export const commandApi = createApi({
  reducerPath: 'commandApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_COMMAND_API_URL,
    prepareHeaders: (headers, { getState }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      const token: string | null = (getState() as RootState).auth.accessToken;
      if (token != null) {
        headers.set('authorization', `Bearer ${token}`);
      }

      return headers;
    }
  }),
  tagTypes: [...cacher.defaultTags, 'FacebookAuthStatus', 'GoogleAuthStatus'],
  endpoints: (builder) => ({
    deleteMultipleRevenues: builder.mutation<
      unknown,
      { siteId: string; revenue: DeleteRevenueRequest[]; componentId: string }
    >({
      async queryFn(request, api, _, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        const mutation = baseQuery({
          url: `${request.siteId}/revenue`,
          method: 'DELETE',
          body: request.revenue
        });

        await mutation;

        return defaultSuccessResult;
      }
    }),
    deleteMultipleOpportunities: builder.mutation<
      unknown,
      {
        siteId: string;
        opportunities: DeleteOpportunityRequest[];
        componentId: string;
      }
    >({
      async queryFn(request, api, extraOptions, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        const mutation = baseQuery({
          url: `${request.siteId}/opportunity`,
          method: 'DELETE',
          body: request.opportunities
        });

        await mutation;

        return defaultSuccessResult;
      }
    }),
    validateWebhookEndpoint: builder.mutation<
      any,
      {
        siteId: string;
        url: string;
      }
    >({
      async queryFn({ siteId, url }, api, _, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/webhooks/validate`,
          method: 'POST',
          body: { url }
        });
      }
    }),
    createWebhook: builder.mutation<
      any,
      {
        siteId: string;
        webhookName: string;
        webhookDescription: string;
        webhookType: string;
        webhookUrl: string;
        isInternalWebhook: boolean;
        useCustomDataSchema: boolean;
        dataFields: WebhookDataField[];
      }
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(queryApi.util.invalidateTags(['Webhooks']));
      },
      async queryFn(
        {
          siteId,
          webhookName,
          webhookDescription,
          webhookType,
          webhookUrl,
          isInternalWebhook,
          useCustomDataSchema,
          dataFields
        },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/webhooks`,
          method: 'POST',
          body: {
            siteId,
            webhookName,
            webhookDescription,
            webhookType,
            webhookUrl,
            isInternalWebhook,
            useCustomDataSchema,
            dataFields
          }
        });
      }
    }),
    updateWebhook: builder.mutation<any, Webhook>({
      async onQueryStarted(webhookBeingUpdated, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        const siteId = webhookBeingUpdated.siteId;

        dispatch(
          queryApi.util.updateQueryData('getWebhooks', { siteId }, (draft) => {
            const existingWebhooks = draft.webhooks;
            const webhookIndex = existingWebhooks.findIndex(
              (w) => w.id === webhookBeingUpdated.id
            );

            existingWebhooks[webhookIndex] = webhookBeingUpdated;
          })
        );
      },
      async queryFn(webhook, api, _, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${webhook.siteId}/webhooks/${webhook.id}`,
          method: 'PATCH',
          body: { status: webhook.status }
        });
      }
    }),
    triggerTaskRun: builder.mutation<
      any,
      {
        siteId: string;
        machineLearningModel: string;
      }
    >({
      async onQueryStarted(taskBeingTriggered, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        const siteId = taskBeingTriggered.siteId;

        dispatch(
          queryApi.util.updateQueryData(
            'getDataScienceTasks',
            { siteId },
            (draft) => {
              const existingDataScienceTasks = draft;
              const dataScienceTaskIndex = existingDataScienceTasks.findIndex(
                (dt) =>
                  dt.machineLearningModel ===
                  taskBeingTriggered.machineLearningModel
              );

              existingDataScienceTasks[dataScienceTaskIndex].status =
                DataScienceTaskStatus.PROCESSING;
            }
          )
        );
      },
      async queryFn({ siteId, machineLearningModel }, api, _, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/data-science/task/${machineLearningModel.toLocaleLowerCase()}/trigger`,
          method: 'POST',
          body: {
            siteId,
            machineLearningModel
          }
        });
      }
    }),
    triggerDataScienceDeployment: builder.mutation<
      any,
      {
        siteId: string;
        machineLearningModel: string;
      }
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(queryApi.util.invalidateTags(['DataScienceTasks']));
      },
      async queryFn({ siteId, machineLearningModel }, api, _, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/data-science/task/${machineLearningModel}/deploy`,
          method: 'POST',
          body: {
            siteId,
            machineLearningModel
          }
        });
      }
    }),
    createDataScienceTask: builder.mutation<
      any,
      {
        siteId: string;
        dataScienceTaskRequest: DataScienceTaskParametersDto;
      }
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(queryApi.util.invalidateTags(['DataScienceTasks']));
      },
      async queryFn({ siteId, dataScienceTaskRequest }, api, _, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/data-science/task/${dataScienceTaskRequest.machineLearningModel}`,
          method: 'POST',
          body: { ...dataScienceTaskRequest, siteId }
        });
      }
    }),
    updateDataScienceTask: builder.mutation<
      any,
      {
        siteId: string;
        dataScienceTask: DataScienceTask;
      }
    >({
      async onQueryStarted(
        dataScienceTaskBeingUpdated,
        { dispatch, queryFulfilled }
      ) {
        await queryFulfilled;

        dispatch(queryApi.util.invalidateTags(['DataScienceTasks']));
      },
      async queryFn({ siteId, dataScienceTask }, api, _, baseQuery) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/data-science/task/${dataScienceTask.machineLearningModel}`,
          method: 'PATCH',
          body: {
            status: dataScienceTask.status,
            autoDeployModelOnTrainingComplete:
              dataScienceTask.autoDeployModelOnTrainingComplete,
            siteId,
            pipelineParametersOverrides:
              dataScienceTask.pipelineParameterOverrides.reduce(
                (acc, parameter) => ({
                  ...acc,
                  [parameter.name]: parameter.value
                }),
                {}
              )
          }
        });
      }
    }),
    createExclusion: builder.mutation<
      any,
      {
        siteId: string;
        exclusionLevel: string;
        exclusionCategory: string;
        exclusionType: ExclusionTypeLabel;
        exclusionMatchTarget: string;
        additionalInformation: string;
        ruleMatchType: string;
        value: string;
      }
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(queryApi.util.invalidateTags(['Exclusions']));
      },
      async queryFn(
        {
          siteId,
          exclusionLevel,
          exclusionCategory,
          exclusionType,
          exclusionMatchTarget,
          additionalInformation,
          ruleMatchType,
          value
        },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/exclusions`,
          method: 'PUT',
          body: {
            siteId,
            exclusionLevel,
            exclusionCategory,
            exclusionType: exclusionTypeLabelToTypeMap[exclusionType],
            exclusionMatchTarget,
            additionalInformation,
            ruleMatchType,
            value
          }
        });
      }
    }),
    deleteExclusion: builder.mutation<
      any,
      {
        siteId: string;
        exclusionLevel: string;
        exclusionCategory: string;
        exclusionType: ExclusionTypeLabel;
        exclusionMatchTarget: string;
        additionalInformation: string;
        ruleMatchType: string;
        value: string;
      }
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(queryApi.util.invalidateTags(['Exclusions']));
      },
      async queryFn(
        {
          siteId,
          exclusionLevel,
          exclusionCategory,
          exclusionType,
          exclusionMatchTarget,
          additionalInformation,
          ruleMatchType,
          value
        },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/exclusions`,
          method: 'DELETE',
          body: {
            siteId,
            exclusionLevel,
            exclusionCategory,
            exclusionType: exclusionTypeLabelToTypeMap[exclusionType],
            exclusionMatchTarget,
            additionalInformation,
            ruleMatchType,
            value
          }
        });
      }
    }),
    updateSegmentationConfigStatus: builder.mutation<
      any,
      {
        siteId: string;
        configId: string;
        updatedConfigStatus: number;
        category: TabKey | null;
      }
    >({
      async onQueryStarted({ category }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          const cacheTag =
            category === TabKey.Forms
              ? 'WebPageViewSegmentationConfigs'
              : 'AdCostSegmentationConfigs';

          dispatch(queryApi.util.invalidateTags([cacheTag]));
        } catch (error) {
          console.error('Error invalidating cache:', error);
        }
      },

      async queryFn(
        { siteId, configId, updatedConfigStatus, category },
        api,
        _,
        baseQuery
      ) {
        const endpoint =
          category === TabKey.Forms
            ? `${siteId}/web-touchpoints/segmentation/config/status/update`
            : `${siteId}/adcosts/segmentation/config//status/update`;

        return await baseQuery({
          url: endpoint,
          method: 'PATCH',
          body: {
            SiteId: siteId,
            ConfigId: configId,
            ConfigStatus: 0,
            UpdatedConfigStatus: updatedConfigStatus
          }
        });
      }
    }),
    createSegmentationConfig: builder.mutation<
      any,
      { category: TabKey | null } & CreateSegmentationConfigRequestDto
    >({
      async onQueryStarted({ category }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            queryApi.util.invalidateTags([
              category === TabKey.Forms
                ? 'WebPageViewSegmentationConfigs'
                : 'AdCostSegmentationConfigs'
            ])
          );
        } catch (error) {
          console.error('Failed to create segmentation config:', error);
        }
      },
      async queryFn(
        {
          category,
          siteId,
          ruleAccessType,
          name,
          createdDateTime,
          createdBy,
          segmentationType,
          segmentName,
          segmentValue,
          rules
        },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        const endpoint =
          category === TabKey.Forms
            ? 'web-touchpoints/segmentation/config'
            : 'adcosts/segmentation/config';

        return await baseQuery({
          url: `${siteId}/${endpoint}`,
          method: 'POST',
          body: {
            siteId,
            ruleAccessType,
            name,
            createdDateTime,
            createdBy,
            segmentationType,
            segmentName,
            segmentValue,
            rules
          }
        });
      }
    }),
    updateSegmentationConfig: builder.mutation<
      any,
      { category: TabKey | null } & UpdateSegmentationConfigRequestDto
    >({
      async onQueryStarted(
        { category, ...requestDto },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;

          dispatch(
            queryApi.util.invalidateTags([
              category === TabKey.Forms
                ? 'WebPageViewSegmentationConfigs'
                : 'AdCostSegmentationConfigs'
            ])
          );
          dispatch(
            queryApi.util.invalidateTags([
              category === TabKey.Forms
                ? 'WebPageViewSegmentationConfig'
                : 'AdCostSegmentationConfig'
            ])
          );
        } catch (error) {
          console.error('Failed to update segmentation config:', error);
        }
      },

      async queryFn(
        {
          id,
          siteId,
          category,
          ruleAccessType,
          name,
          updatedDateTime,
          updatedBy,
          segmentationType,
          segmentName,
          segmentValue,
          rules
        },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        const endpoint =
          category === TabKey.Forms
            ? `web-touchpoints/segmentation/config/update`
            : `adcosts/segmentation/config/update`;

        return await baseQuery({
          url: `${siteId}/${endpoint}`,
          method: 'PATCH',
          body: {
            id,
            siteId,
            status: 'Active',
            ruleAccessType,
            name,
            updatedDateTime,
            updatedBy,
            segmentationType,
            segmentName,
            segmentValue,
            rules
          }
        });
      }
    }),
    googleAdsTokenHandler: builder.mutation({
      async queryFn(
        data: {
          siteId: string;
          authenticationCode: string;
        },
        api,
        _,
        baseQuery
      ) {
        const { siteId, authenticationCode } = data;
        return await baseQuery({
          url: `${siteId}/client-integrations/google-ads/token`,
          method: 'POST',
          body: { siteId, authenticationCode }
        });
      }
    }),
    googleAdsAuthenticationStatus: builder.query<
      {
        isAuthenticated: boolean;
        accountIdLookups: { id: string; accountId: string }[];
      },
      string
    >({
      async queryFn(
        siteId,
        api,
        _,
        baseQuery
      ): Promise<
        QueryReturnValue<
          {
            isAuthenticated: boolean;
            accountIdLookups: { id: string; accountId: string }[];
          },
          FetchBaseQueryError
        >
      > {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return {
            data: {
              isAuthenticated: true,
              accountIdLookups: [
                { id: 'sandbox-google-id-1', accountId: '111111' }
              ]
            }
          };
        }

        const rawResponse = await baseQuery({
          url: `${siteId}/client-integrations/google-ads/authentication-status`
        });

        if ('data' in rawResponse && rawResponse.data) {
          const response = rawResponse.data as {
            IsAuthenticated: boolean;
            AccountIdLookups?: { Id: string; AccountId: string }[];
          };

          return {
            data: {
              isAuthenticated: response.IsAuthenticated,
              accountIdLookups:
                response.AccountIdLookups?.map((lookUps) => ({
                  id: lookUps.Id,
                  accountId: lookUps.AccountId
                })) ?? []
            }
          };
        }

        return rawResponse as QueryReturnValue<
          {
            isAuthenticated: boolean;
            accountIdLookups: { id: string; accountId: string }[];
          },
          FetchBaseQueryError
        >;
      },
      providesTags: cacher.cacheByQueryArg('GoogleAuthStatus')
    }),
    completeGoogleAdsIntegrationSetup: builder.mutation({
      async queryFn(
        data: {
          siteId: string;
          accountIds: string[];
          createdBy: string;
          updatedBy: string;
          platformConfigs: {
            configIdentifier: string;
            queryFilters: {
              field: string;
              operator: string;
              value: string;
            }[];
          }[];
        },
        api,
        _,
        baseQuery
      ) {
        const { siteId, accountIds, createdBy, platformConfigs } = data;

        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/client-integrations/google-ads`,
          method: 'PUT',
          body: {
            siteId,
            accountIds,
            createdBy,
            updatedBy: createdBy,
            platformConfigs
          }
        });
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(queryApi.util.invalidateTags(['ClientIntegrations']));
      }
    }),
    facebookAdsTokenHandler: builder.mutation({
      async queryFn(
        data: {
          siteId: string;
          authenticationCode: string;
        },
        api,
        _,
        baseQuery
      ) {
        const { siteId, authenticationCode } = data;
        return await baseQuery({
          url: `${siteId}/client-integrations/facebook-ads/token`,
          method: 'POST',
          body: { siteId, authenticationCode }
        });
      }
    }),
    facebookAdsAuthenticationStatus: builder.query<
      {
        isAuthenticated: boolean;
        accountIdLookups: { id: string; accountId: string }[];
      },
      string
    >({
      async queryFn(
        siteId,
        api,
        _,
        baseQuery
      ): Promise<
        QueryReturnValue<
          {
            isAuthenticated: boolean;
            accountIdLookups: { id: string; accountId: string }[];
          },
          FetchBaseQueryError
        >
      > {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return {
            data: {
              isAuthenticated: true,
              accountIdLookups: [
                { id: 'sandbox-facebook-id-1', accountId: '123456' }
              ]
            }
          };
        }

        const rawResponse = await baseQuery({
          url: `${siteId}/client-integrations/facebook-ads/authentication-status`
        });

        if ('data' in rawResponse && rawResponse.data) {
          const response = rawResponse.data as {
            IsAuthenticated: boolean;
            AccountIdLookups?: { Id: string; AccountId: string }[];
          };

          return {
            data: {
              isAuthenticated: response.IsAuthenticated,
              accountIdLookups:
                response.AccountIdLookups?.map((lookUps) => ({
                  id: lookUps.Id,
                  accountId: lookUps.AccountId
                })) ?? []
            }
          };
        }

        return rawResponse as QueryReturnValue<
          {
            isAuthenticated: boolean;
            accountIdLookups: { id: string; accountId: string }[];
          },
          FetchBaseQueryError
        >;
      },
      providesTags: cacher.cacheByQueryArg('FacebookAuthStatus')
    }),
    completeFacebookAdsIntegrationSetup: builder.mutation({
      async queryFn(
        data: {
          siteId: string;
          accountIds: string[];
          createdBy: string;
          updatedBy: string;
          platformConfigs: {
            configIdentifier: string;
            queryFilters: {
              field: string;
              operator: string;
              value: string;
            }[];
          }[];
        },
        api,
        _,
        baseQuery
      ) {
        const { siteId, accountIds, createdBy, platformConfigs } = data;

        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/client-integrations/facebook-ads`,
          method: 'PUT',
          body: {
            siteId,
            accountIds,
            createdBy,
            updatedBy: createdBy,
            platformConfigs
          }
        });
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(queryApi.util.invalidateTags(['ClientIntegrations']));
      }
    }),
    manageClientIntegrationState: builder.mutation<
      any,
      {
        siteId: string;
        platformCategory: string;
        platform: string;
        clientIntegrationType: string;
        newState: string;
      }
    >({
      async queryFn(
        { siteId, platformCategory, platform, clientIntegrationType, newState },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/client-integrations/${platformCategory}/${platform}/${clientIntegrationType}/state`,
          method: 'PATCH',
          body: {
            SiteId: siteId,
            PlatformCategory: platformCategory,
            Platform: platform,
            Type: clientIntegrationType,
            NewState: newState
          }
        });
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(queryApi.util.invalidateTags(['ClientIntegrations']));
      }
    }),
    deleteClientIntegration: builder.mutation<
      any,
      {
        siteId: string;
        platformCategory: string;
        platform: string;
        clientIntegrationType: string;
      }
    >({
      async queryFn(
        { siteId, platformCategory, platform, clientIntegrationType },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/client-integrations/${platformCategory}/${platform}/${clientIntegrationType}`,
          method: 'DELETE'
        });
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(queryApi.util.invalidateTags(['ClientIntegrations']));
      }
    }),
    runClientIntegration: builder.mutation<
      any,
      {
        siteId: string;
        platformCategory: string | undefined;
        platform: string | undefined;
        clientIntegrationType: string | undefined;
        body: {
          startDate: string;
          endDate: string;
        };
      }
    >({
      async queryFn(
        { siteId, platformCategory, platform, clientIntegrationType, body },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/client-integrations/${platformCategory}/${platform}/${clientIntegrationType}/run`,
          method: 'POST',
          body
        });
      }
    }),
    upsertSiteCurrencyCodeConfig: builder.mutation<
      any,
      {
        siteId: string;
        reportableCurrencyCodes: string[];
        updatedBy: string;
      }
    >({
      async queryFn(
        { siteId, reportableCurrencyCodes, updatedBy },
        api,
        _,
        baseQuery
      ) {
        const isSandboxUser = selectIsSandboxUser(api.getState() as RootState);

        if (isSandboxUser) {
          return defaultSuccessResult;
        }

        return await baseQuery({
          url: `${siteId}/currencies/config`,
          method: 'PATCH',
          body: {
            SiteId: siteId,
            ReportableCurrencyCodes: reportableCurrencyCodes,
            UpdatedBy: updatedBy
          }
        });
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(queryApi.util.invalidateTags(['Currencies-Config']));
      }
    })
  })
});

export const {
  useDeleteMultipleRevenuesMutation,
  useDeleteMultipleOpportunitiesMutation,
  useValidateWebhookEndpointMutation,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useTriggerTaskRunMutation,
  useCreateDataScienceTaskMutation,
  useUpdateDataScienceTaskMutation,
  useCreateExclusionMutation,
  useDeleteExclusionMutation,
  useUpdateSegmentationConfigStatusMutation,
  useCreateSegmentationConfigMutation,
  useUpdateSegmentationConfigMutation,
  useGoogleAdsTokenHandlerMutation,
  useGoogleAdsAuthenticationStatusQuery,
  useCompleteGoogleAdsIntegrationSetupMutation,
  useManageClientIntegrationStateMutation,
  useDeleteClientIntegrationMutation,
  useTriggerDataScienceDeploymentMutation,
  useRunClientIntegrationMutation,
  useFacebookAdsAuthenticationStatusQuery,
  useFacebookAdsTokenHandlerMutation,
  useCompleteFacebookAdsIntegrationSetupMutation,
  useUpsertSiteCurrencyCodeConfigMutation
} = commandApi;
